import * as Y from 'yjs';
import { keyAfter } from './surface-order';
import { SURFACES_ROOT, readSurfaces } from './document-surfaces';

/**
 * Manifest entries a document may hold, not counting the body (ui-design.md
 * §7.1). Raised from 10 to 25 for multi-recording: a recording contributes up
 * to four surfaces, so 10 capped a document at two meetings.
 */
export const SURFACE_CAP = 25;

export interface NewSurface {
  title: string;
  surface: 'text' | 'canvas' | 'audio';
  /** Template key that produced this surface; null means a person authored it. */
  generator?: string | null;
  /** Mint the id up front when the caller needs it before the write — to focus
   *  the surface it is about to create, say. Refused if it already names one. */
  id?: string;
}

/**
 * Appends a surface to a document's manifest, returning its id — or `null` if
 * refused: no doc yet, the document is at the cap, the order key ran out, or
 * the explicit id is taken.
 *
 * Lives here rather than in `useSurfaceMutations` because creating a surface
 * is not only a thing the surface panel does: the homepage creates one on a
 * brand-new document, and `lib/` is what `app/` and `components/` can reach.
 * The mutations hook delegates to this, so both write the manifest one way.
 */
export function createSurface(doc: Y.Doc | null, entry: NewSurface): string | null {
  if (!doc) return null;

  const entries = readSurfaces(doc);
  if (entries.length >= SURFACE_CAP) return null;
  if (entry.id !== undefined && entries.some((e) => e.id === entry.id)) return null;

  const last = entries.length > 0 ? entries[entries.length - 1] : null;
  const order = keyAfter(last?.order ?? null);
  if (order === null) return null;

  const id = entry.id ?? generateId(new Set(entries.map((e) => e.id)));
  // Always derived from the id actually used, so the two cannot diverge.
  const fragment = `surface_${id}`;

  doc.transact(() => {
    const map = new Y.Map<unknown>();
    map.set('id', id);
    map.set('title', entry.title);
    map.set('fragment', fragment);
    map.set('surface', entry.surface);
    map.set('source', 'owned');
    map.set('order', order);
    map.set('editable', true);
    map.set('generator', entry.generator ?? null);
    map.set('status', 'ready');
    doc.getArray<Y.Map<unknown>>(SURFACES_ROOT).push([map]);
  });

  return id;
}

function generateId(existing: ReadonlySet<string>): string {
  let id: string;
  do {
    id = crypto.randomUUID();
  } while (existing.has(id));
  return id;
}
